/**
 * AOP 2027 Planning — edit or remove one AOP draft.
 *
 * A copy of the Org Planning Overview's `EditRoleModal`, reduced to what a draft needs and calling
 * `/api/aop-planning` only — kept apart so the regular edit flow is untouched. A draft has no
 * approvals, so there is no 10% rule and no re-approval step: every change is saved straight away
 * and logged on the row. "Remove from the AOP" takes the draft out (`AOP [cancelled]`); it asks twice,
 * so a role never leaves the plan by a slip.
 *
 * ⚠️ Native `<select>` / `<input>`, the same documented deviation as the original form.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { Button } from '@finn-auto/design-system/atoms/button';
import { fetchProposalFieldOptions, type OrgNode } from '../../services/internalPlaygroundApi';
import { RoleEditValidationError, type RoleEditValues } from '../../services/plannedRoleEditsApi';
import {
  withoutCacChoice,
  AOP_TIMELINE_QUERY_KEY,
  fetchAopDraft,
  removeAopDraft,
  saveAopDraft,
  type AopDraft,
} from '../../services/aopPlanningApi';
import { CompensationSummary } from '../../components/CompensationSummary';
import { useRoleCompensation } from '../internalPlayground/useRoleCompensation';
import { FIELD_CLASS, hiringManagerOptions, unitOptionsForDepartment } from '../internalPlayground/proposeRole';
import { ChoiceToggle, Field } from '../internalPlayground/proposeRoleUi';
import { changedValues, withCurrent } from '../internalPlayground/editRole';
import { useViewerScope } from '../internalPlayground/viewerScope';

/** Edit or remove one AOP draft — loads the record fresh, then the form. */
export function AopEditModal({
  recordId,
  label,
  root,
  onClose,
  onWithdrawn,
  inReview = false,
}: {
  recordId: string;
  label: string;
  root: OrgNode;
  onClose: () => void;
  /** A change withdrew these departments' submissions — the page says so after the form closes. */
  onWithdrawn?: (departments: string[]) => void;
  /** Opened from the Approvals tab: a submitted department stays submitted with its ticks, and the
   * role is set aside there rather than removed here. */
  inReview?: boolean;
}) {
  const draftQuery = useQuery({
    queryKey: ['aop-draft', recordId],
    queryFn: () => fetchAopDraft(recordId),
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  return (
    <Modal open onOpenChange={(open: boolean) => !open && onClose()}>
      <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,40rem)] gap-4 overflow-auto rounded p-6">
        <ModalHeader>
          <ModalTitle className="!text-xl !font-semibold">Edit 2027 hire: {label}</ModalTitle>
        </ModalHeader>
        {draftQuery.isPending && <p className="text-sm text-zinc-500">Loading the role…</p>}
        {draftQuery.error && (
          <>
            <p className="text-sm text-red-600">
              {draftQuery.error instanceof Error ? draftQuery.error.message : 'Could not load the role.'}
            </p>
            <div className="flex justify-end">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          </>
        )}
        {draftQuery.data && (
          <AopEditForm draft={draftQuery.data.role} root={root} onClose={onClose} onWithdrawn={onWithdrawn} inReview={inReview} />
        )}
      </ModalContent>
    </Modal>
  );
}

/** The edit form for a loaded draft; saves only the changed fields. */
function AopEditForm({
  draft,
  root,
  onClose,
  onWithdrawn,
  inReview = false,
}: {
  draft: AopDraft;
  root: OrgNode;
  onClose: () => void;
  onWithdrawn?: (departments: string[]) => void;
  inReview?: boolean;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<RoleEditValues>(draft.values);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof RoleEditValues, string>>>({});
  const [confirmRemove, setConfirmRemove] = useState(false);
  const changes = useMemo(() => changedValues(draft.values, form), [draft.values, form]);
  const dirty = Object.keys(changes).length > 0;

  const optionsQuery = useQuery({
    queryKey: ['internal-playground-proposal-options'],
    queryFn: fetchProposalFieldOptions,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  // No CAC on the AOP page — see `withoutCacChoice`.
  const options = useMemo(() => withoutCacChoice(optionsQuery.data), [optionsQuery.data]);

  const scope = useViewerScope();
  const managers = useMemo(() => {
    // A department lead may only move the role to someone in their own line (the proxy refuses others).
    const list = hiringManagerOptions(root).filter((option) => scope.inLine(option.employeeId));
    // Keep the current manager pickable even if they sit outside the tree, or the select would show
    // someone else and a save would appear to change the manager.
    const current = draft.values.hiringManagerEmployeeId;
    if (current && !list.some((m) => m.employeeId === current)) {
      return [{ employeeId: current, name: draft.hiringManagerName || current }, ...list];
    }
    return list;
  }, [root, scope, draft.values.hiringManagerEmployeeId, draft.hiringManagerName]);

  const pricedRows = useMemo(
    () => [{ trackAndLevel: form.trackAndLevel, plannedStartDate: form.startDate }],
    [form.trackAndLevel, form.startDate]
  );
  const compensation = useRoleCompensation(form.role, form.peopleTeam, pricedRows);

  /** Refetch the page after a save or a removal. */
  const refresh = () => void queryClient.invalidateQueries({ queryKey: AOP_TIMELINE_QUERY_KEY });
  const save = useMutation({
    mutationFn: () => saveAopDraft(draft.id, changes, { inReview }),
    onSuccess: (result) => {
      refresh();
      if (result.withdrawnFrom.length) onWithdrawn?.(result.withdrawnFrom);
      onClose();
    },
    onError: (error) => setFieldErrors(error instanceof RoleEditValidationError ? error.details : {}),
  });
  const removal = useMutation({
    mutationFn: () => removeAopDraft(draft.id),
    onSuccess: (result) => {
      refresh();
      if (result.withdrawnFrom.length) onWithdrawn?.(result.withdrawnFrom);
      onClose();
    },
    onError: () => setConfirmRemove(false),
  });
  const busy = save.isPending || removal.isPending;

  const set = (key: keyof RoleEditValues) => (value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const unitOptions = useMemo(
    () => withCurrent(unitOptionsForDepartment(options?.unit ?? [], form.peopleTeam), form.unit),
    [options, form.peopleTeam, form.unit]
  );

  const selectField = (key: 'role' | 'peopleTeam' | 'unit' | 'trackAndLevel') => {
    const choices = key === 'unit' ? unitOptions : withCurrent(options?.[key] ?? [], draft.values[key]);
    const onChange = (value: string) => {
      if (key === 'peopleTeam') {
        const nextUnits = unitOptionsForDepartment(options?.unit ?? [], value);
        set('peopleTeam')(value);
        setForm((prev) => ({ ...prev, unit: prev.unit && nextUnits.includes(prev.unit) ? prev.unit : '' }));
        return;
      }
      set(key)(value);
    };
    return (
      <select
        id={`aop-edit-${key}`}
        className={FIELD_CLASS}
        value={form[key] ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={!options}
      >
        <option value="">{options ? '—' : optionsQuery.isPending ? 'Loading…' : 'Unavailable'}</option>
        {choices.map((choice) => (
          <option key={choice} value={choice}>
            {choice}
          </option>
        ))}
      </select>
    );
  };

  const toggleField = (key: 'reasonForHiring' | 'costCategory') => (
    <ChoiceToggle
      labelledBy={`aop-edit-${key}-label`}
      options={withCurrent(options?.[key] ?? [], draft.values[key])}
      value={form[key] ?? ''}
      onChange={set(key)}
      disabled={!options}
    />
  );

  const saveError = save.error && !(save.error instanceof RoleEditValidationError) ? save.error : null;

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        // `loading` on the DS Button only blocks the mouse; Enter still submits.
        if (busy || !dirty) return;
        save.mutate();
      }}
    >
      <p className="text-sm text-zinc-500">
        Changes your AOP 2027 draft. Nothing goes to the approvers yet, so every change is saved straight away.
      </p>

      <Field label="Title" htmlFor="aop-edit-title" required error={fieldErrors.title}>
        <input
          id="aop-edit-title"
          className={FIELD_CLASS}
          value={form.title}
          onChange={(e) => set('title')(e.target.value)}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Role" htmlFor="aop-edit-role" required error={fieldErrors.role}>
          {selectField('role')}
        </Field>
        <Field label="Department (Reporting Line)" htmlFor="aop-edit-peopleTeam" required error={fieldErrors.peopleTeam}>
          {selectField('peopleTeam')}
        </Field>
        <Field
          label="Hiring manager"
          htmlFor="aop-edit-hiringManagerEmployeeId"
          required
          error={fieldErrors.hiringManagerEmployeeId}
        >
          <select
            id="aop-edit-hiringManagerEmployeeId"
            className={FIELD_CLASS}
            value={form.hiringManagerEmployeeId}
            onChange={(e) => set('hiringManagerEmployeeId')(e.target.value)}
          >
            <option value="">—</option>
            {managers.map((m) => (
              <option key={m.employeeId} value={m.employeeId}>
                {m.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Planned start date" htmlFor="aop-edit-startDate" required error={fieldErrors.startDate}>
          <input
            id="aop-edit-startDate"
            type="date"
            className={FIELD_CLASS}
            value={form.startDate}
            onChange={(e) => set('startDate')(e.target.value)}
          />
        </Field>
        <Field label="Unit" htmlFor="aop-edit-unit" error={fieldErrors.unit}>
          {selectField('unit')}
        </Field>
        <Field label="Track and level" htmlFor="aop-edit-trackAndLevel" error={fieldErrors.trackAndLevel}>
          {selectField('trackAndLevel')}
        </Field>
        <Field label="Reason for hiring" labelId="aop-edit-reasonForHiring-label" error={fieldErrors.reasonForHiring}>
          {toggleField('reasonForHiring')}
        </Field>
        <Field label="Cost category" labelId="aop-edit-costCategory-label" error={fieldErrors.costCategory}>
          {toggleField('costCategory')}
        </Field>
      </div>

      <CompensationSummary compensation={compensation.first} busy={compensation.isPending} />

      <Field label="Why this role?" htmlFor="aop-edit-proposalRational" required error={fieldErrors.proposalRational}>
        <textarea
          id="aop-edit-proposalRational"
          rows={4}
          className={FIELD_CLASS}
          value={form.proposalRational ?? ''}
          onChange={(e) => set('proposalRational')(e.target.value)}
        />
      </Field>

      {(saveError || removal.error) && (
        <p role="alert" className="text-xs text-red-600">
          {[saveError, removal.error].find(Boolean) instanceof Error
            ? ([saveError, removal.error].find(Boolean) as Error).message
            : 'Could not save the change.'}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        {/* In review a role is set aside on the Approvals tab, never removed from here. */}
        {!inReview && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mr-auto"
            loading={removal.isPending}
            disabled={busy}
            onClick={() => {
              if (busy) return;
              if (!confirmRemove) setConfirmRemove(true);
              else removal.mutate();
            }}
          >
            {confirmRemove ? 'Click again to remove' : 'Remove from the AOP'}
          </Button>
        )}
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="sm" loading={save.isPending} disabled={!dirty || busy}>
          Save changes
        </Button>
      </div>
    </form>
  );
}
